/**
 * Per-submission metric computation for % Content Done, % Document Done,
 * and Consistency.
 *
 * A2F-456: Both signed-off and Consistency were incorrectly using project-
 * wide data. Now computed from the focused submission's docs.
 *
 * A2F-491: Added `computeClearScopeBreakdown` for the three-bucket
 * (thisSub / thisSubAndPrior / allScopes) Clear tile breakdown used
 * by both the dashboard card and the /clear page.
 *
 * A2F-666 (UI tweaks): the legacy "Signed Off" metric was renamed to
 * "% Content Done" and gained a sibling "% Document Done" sourced from
 * `document_owner_assignments`. Both use the same per-doc formula: a doc
 * counts only when it has at least one assignable owner/section, and is
 * "done" only when ALL of those are signed. Docs without owners are
 * surfaced separately by the unassigned-count tiles on Document Readiness,
 * so excluding them from the percentage denominator stops "100%" from
 * masking unassigned work.
 */

import type {
  SectionOwnerScanResponse,
  ConsistencyAggregate,
  DocumentClearanceEntry,
  Submission,
} from '@/types/api';
import type { DocSubmissionMap } from './findingClassification';

/** Per-document owner state from `api.documentReadiness.getAssignments`. */
export interface DocOwnerEntry {
  state: 'pending' | 'signed';
}

/** Map from documentId to its list of owner entries for a given submission. */
export type DocOwnersBySubmissionDoc = Record<string, DocOwnerEntry[]>;

export interface SubmissionMetrics {
  contentDoneCount: number;
  contentDoneTotal: number;
  contentDonePct: number | null;
  contentDoneLabel: string;
  documentDoneCount: number;
  documentDoneTotal: number;
  documentDonePct: number | null;
  documentDoneLabel: string;
  consistencyClean: number;
  consistencyTotal: number;
  consistencyPct: number | null;
  consistencyLabel: string;
}

/**
 * % Content Done: a doc counts only when it has ≥1 owner section, and is
 * "done" only when ALL owner sections in that doc are signed. Docs with
 * no owner sections (untracked / no Word comments) are excluded from the
 * denominator — the "Docs without content owners" tile on Document
 * Readiness catches those.
 */
function computeContentDoneForSubmission(
  sectionOwners: SectionOwnerScanResponse | null,
  submissionDocIds: Set<string>,
): { count: number; total: number } {
  if (!sectionOwners) return { count: 0, total: 0 };

  let count = 0;
  let total = 0;
  for (const d of sectionOwners.documents ?? []) {
    if (!submissionDocIds.has(d.documentId)) continue;
    if (d.ownerSections === 0) continue;
    total++;
    if (d.ownerSectionsSignedOff >= d.ownerSections) count++;
  }
  return { count, total };
}

/**
 * % Document Done: a doc counts only when ≥1 user is assigned as a doc
 * owner, and is "done" only when ALL assigned doc owners have signed.
 * Docs with zero owners are excluded from the denominator — the "Docs
 * without owners" tile on Document Readiness catches those.
 */
function computeDocumentDoneForSubmission(
  docOwners: DocOwnersBySubmissionDoc | null,
  submissionDocIds: Set<string>,
): { count: number; total: number } {
  if (!docOwners) return { count: 0, total: 0 };

  let count = 0;
  let total = 0;
  for (const docId of submissionDocIds) {
    const owners = docOwners[docId] ?? [];
    if (owners.length === 0) continue;
    total++;
    if (owners.every((o) => o.state === 'signed')) count++;
  }
  return { count, total };
}

function safePct(num: number, den: number): number | null {
  if (den === 0) return null;
  return Math.round((num / den) * 100);
}

/**
 * Compose % Content Done + % Document Done + Consistency metrics for one
 * submission.
 *
 * A2F-462 follow-up: Consistency comes from the server aggregate.
 * A2F-666: Document-Done is computed locally from a per-submission map of
 * doc_owner_assignments (fetched once per planning submission).
 */
export function computeSubmissionMetrics(
  submissionDocIds: Set<string>,
  sectionOwners: SectionOwnerScanResponse | null,
  consistencyAggregate: ConsistencyAggregate | null,
  docOwners: DocOwnersBySubmissionDoc | null,
): SubmissionMetrics {
  const content = computeContentDoneForSubmission(sectionOwners, submissionDocIds);
  const document = computeDocumentDoneForSubmission(docOwners, submissionDocIds);

  const clean = consistencyAggregate?.clean ?? 0;
  const total = consistencyAggregate?.total ?? 0;
  const pct = consistencyAggregate?.pct ?? null;

  return {
    contentDoneCount: content.count,
    contentDoneTotal: content.total,
    contentDonePct: safePct(content.count, content.total),
    contentDoneLabel:
      content.total > 0 ? `${content.count} / ${content.total} docs` : 'Not scanned',
    documentDoneCount: document.count,
    documentDoneTotal: document.total,
    documentDonePct: safePct(document.count, document.total),
    documentDoneLabel:
      document.total > 0 ? `${document.count} / ${document.total} docs` : 'Not assigned',
    consistencyClean: clean,
    consistencyTotal: total,
    consistencyPct: pct,
    // A2F-462 denominator fix: total is always 6 (never 0) after the server-
    // side rule change, so the old `total > 0` gate never fires. Gate on
    // pct === null instead so the sublabel matches the donut: "Not scanned"
    // ⟺ scan_status == 'not_scanned' ⟺ pct is null. Same invariant,
    // coherent across all three scan_status states.
    consistencyLabel: pct === null ? 'Not scanned' : `${clean} / ${total} checks`,
  };
}

/**
 * Build a Set of document IDs belonging to a submission from the docSubmissionMap.
 */
export function getDocIdsForSubmission(docSubMap: DocSubmissionMap, submissionId: string): Set<string> {
  const ids = new Set<string>();
  for (const [docId, subId] of docSubMap) {
    if (subId === submissionId) ids.add(docId);
  }
  return ids;
}

/**
 * Merge multiple documentMaps into one (used when combining scan responses).
 */
export function mergeDocumentMaps(...maps: (Record<string, string> | undefined)[]): Record<string, string> {
  const merged: Record<string, string> = {};
  for (const m of maps) {
    if (m) Object.assign(merged, m);
  }
  return merged;
}

// --- A2F-491: Clear scope breakdown -----------------------------------------

/** Three-bucket count of a Clear metric by submission scope. */
export interface ClearCountByScope {
  thisSub: number;
  thisSubAndPrior: number;
  allScopes: number;
}

/** Three-bucket ratio of a Clear metric by submission scope. */
export interface ClearRatioByScope {
  thisSub: { num: number; den: number };
  thisSubAndPrior: { num: number; den: number };
  allScopes: { num: number; den: number };
}

/**
 * All four Chrona Clear metrics broken down by submission scope.
 *
 * Unlike Review's `classifyFindingsByScope` (which relies on the
 * new / pre-existing / inherited provenance of a finding spanning multiple
 * documents), Clear metrics are per-document and each document belongs to
 * exactly one submission. So "this sub + prior" is defined via actual
 * submission ordering (`displaySequence` ascending): a doc counts toward
 * `thisSubAndPrior` iff its submission's sequence is ≤ the focused sub's.
 *
 * Sign-off is section-based (not doc-based) so the dashboard tile matches
 * the /clear page's existing Owner Sign-off tile: `sectionsSignedOff /
 * totalSections` aggregated across the docs in each scope.
 */
export interface ClearScopeBreakdown {
  unresolvedComments: ClearCountByScope;
  trackedChanges: ClearCountByScope;
  ownerSignOff: ClearRatioByScope;
  clearance: ClearRatioByScope;
}

/**
 * Compute a per-scope breakdown of the four Clear metrics from the raw
 * per-document list. Returns null if the inputs are insufficient (no
 * documents, no doc→sub map, or the focused sub is not in the list).
 */
export function computeClearScopeBreakdown(
  documents: DocumentClearanceEntry[] | undefined,
  docSubMap: DocSubmissionMap | null,
  submissions: Submission[],
  focusedSubmissionId: string,
): ClearScopeBreakdown | null {
  if (!documents || !docSubMap) return null;
  const focused = submissions.find(s => s.id === focusedSubmissionId);
  if (!focused) return null;

  const seqBySubId = new Map<string, number>();
  for (const s of submissions) seqBySubId.set(s.id, s.displaySequence);
  const focusedSeq = focused.displaySequence;

  const uc: ClearCountByScope = { thisSub: 0, thisSubAndPrior: 0, allScopes: 0 };
  const tc: ClearCountByScope = { thisSub: 0, thisSubAndPrior: 0, allScopes: 0 };
  const so: ClearRatioByScope = {
    thisSub: { num: 0, den: 0 },
    thisSubAndPrior: { num: 0, den: 0 },
    allScopes: { num: 0, den: 0 },
  };
  const cl: ClearRatioByScope = {
    thisSub: { num: 0, den: 0 },
    thisSubAndPrior: { num: 0, den: 0 },
    allScopes: { num: 0, den: 0 },
  };

  for (const d of documents) {
    const subId = docSubMap.get(d.documentId);
    const docSeq = subId !== undefined ? seqBySubId.get(subId) : undefined;
    const isThisSub = subId === focusedSubmissionId;
    const isThisSubOrPrior = docSeq !== undefined && docSeq <= focusedSeq;

    // allScopes always includes every document
    uc.allScopes += d.unresolvedComments;
    tc.allScopes += d.trackedChanges;
    so.allScopes.num += d.ownerSectionsSignedOff;
    so.allScopes.den += d.ownerSections;
    cl.allScopes.num += d.isClear ? 1 : 0;
    cl.allScopes.den += 1;

    if (isThisSubOrPrior) {
      uc.thisSubAndPrior += d.unresolvedComments;
      tc.thisSubAndPrior += d.trackedChanges;
      so.thisSubAndPrior.num += d.ownerSectionsSignedOff;
      so.thisSubAndPrior.den += d.ownerSections;
      cl.thisSubAndPrior.num += d.isClear ? 1 : 0;
      cl.thisSubAndPrior.den += 1;
    }

    if (isThisSub) {
      uc.thisSub += d.unresolvedComments;
      tc.thisSub += d.trackedChanges;
      so.thisSub.num += d.ownerSectionsSignedOff;
      so.thisSub.den += d.ownerSections;
      cl.thisSub.num += d.isClear ? 1 : 0;
      cl.thisSub.den += 1;
    }
  }

  return { unresolvedComments: uc, trackedChanges: tc, ownerSignOff: so, clearance: cl };
}

/** Format a Clear ratio as "X/Y", or "—" when the denominator is 0. */
export function formatClearRatio(r: { num: number; den: number }): string {
  if (r.den === 0) return '—';
  return `${r.num}/${r.den}`;
}

/** Convert a Clear ratio to an integer percent, or null when undefined. */
export function clearRatioPercent(r: { num: number; den: number | null | undefined }): number | null {
  if (r.den == null || r.den === 0) return null; // == null catches null AND undefined
  return Math.round((r.num / r.den) * 100);
}

/** Format a Clear ratio as an integer percentage string (e.g. "73%"), or "—" when undefined. */
export function formatClearPercent(r: { num: number; den: number | null | undefined }): string {
  const p = clearRatioPercent(r);
  return p === null ? '—' : `${p}%`;
}
